import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import {
  apiCallVenuePreferencesAdd,
  apiCallVenuePreferencesUpdate,
  apiCallVenuePreferences,
  apiCallDaysList,
  apiCallGetPreferencesMasterData,
} from "../../../../Core/Apicall";
import ChipSelector from "./ChipSelectorProps";
import { useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { toast, ToastContainer } from "react-toastify";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";

interface VenueStarAdminProps {
  venueId: string;
}

interface ClaimMediaPayload {
  _id: string;
  venueId: string;
  userId: string;
  daysIds: string[];
}

const preferenceSections = [
  {
    key: "entryFees",
    title: "Entry Fee",
    labelKey: "entryFeeName",
  },
  {
    key: "cuisines",
    title: "Cuisine",
    labelKey: "cuisineName",
  },
  {
    key: "alcohols",
    title: "Alcohol",
    labelKey: "alcoholName",
  },
  {
    key: "nonAlcohols",
    title: "Non Alcohol",
    labelKey: "nonAlcoholName",
  },
  {
    key: "regions",
    title: "Region",
    labelKey: "regionName",
  },
  {
    key: "specials",
    title: "Special",
    labelKey: "specialName",
  },
  {
    key: "liveMusics",
    title: "Live Music",
    labelKey: "liveMusicName",
  },
  {
    key: "djClubs",
    title: "DJ Club",
    labelKey: "djClubName",
  },
  {
    key: "cravings",
    title: "Craving",
    labelKey: "cravingName",
  },
  {
    key: "foodPrices",
    title: "Food Price",
    labelKey: "foodPriceName",
  },
  {
    key: "dietaries",
    title: "Dietary",
    labelKey: "dietaryName",
  },
  {
    key: "eventActivities",
    title: "Event Activity",
    labelKey: "eventActivityName",
  },
];

const VenuePreferences: React.FC<VenueStarAdminProps> = ({ venueId }) => {
  const [editingRow, setEditingRow] = useState<ClaimMediaPayload | null>(null);
  const [days, setDays] = useState<any[]>([]);
  const [preferenceData, setPreferenceData] = useState<any>({});
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<
    Record<string, string[]>
  >({});

  const [loading, setLoading] = useState(true);
  const userdata: any = useSelector((state: RootState) => state.global.token);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchLists();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (days.length > 0 && selectedDays.length === 0) {
      const firstDay = days[0]._id;
      setSelectedDays([firstDay]);
      fetchDayPreference(firstDay);
    }
  }, [days]);

  const fetchLists = async () => {
    try {
      const [daysRes, masterData] = await Promise.all([
        apiCallDaysList({}),
        apiCallGetPreferencesMasterData({ venueId }),
      ]);

      if (daysRes?.data) {
        setDays(daysRes.data);
      }

      if (masterData?.data) {
        setPreferenceData(masterData.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDayPreference = async (dayId: string) => {
    try {
      setLoading(true);

      const res = await apiCallVenuePreferences({
        venueId,
        daysIds: [dayId],
      });

      if (res?.data) {
        const pref = res.data;

        setEditingRow(pref);

        setSelectedQuestions({
          entryFees: pref.entryFeeIds?.map((i: any) => i._id) || [],
          cuisines: pref.cuisineIds?.map((i: any) => i._id) || [],
          alcohols: pref.alcoholIds?.map((i: any) => i._id) || [],
          nonAlcohols: pref.nonAlcoholIds?.map((i: any) => i._id) || [],
          regions: pref.regionsIds?.map((i: any) => i._id) || [],
          specials: pref.specialIds?.map((i: any) => i._id) || [],
          liveMusics: pref.liveMusicIds?.map((i: any) => i._id) || [],
          djClubs: pref.djClubIds?.map((i: any) => i._id) || [],
          cravings: pref.cravingIds?.map((i: any) => i._id) || [],
          foodPrices: pref.foodPriceIds?.map((i: any) => i._id) || [],
          dietaries: pref.dietaryIds?.map((i: any) => i._id) || [],
          eventActivities:
            pref.eventActivitiesIds?.map((i: any) => i._id) || [],
        });
      } else {
        setEditingRow(null);
        setSelectedQuestions({});
      }
    } catch (error) {
      setSelectedQuestions({});
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const currentDayId = selectedDays[0];

    const data = {
      venueId,
      userId: userdata?.data?.userId,
      daysIds: [currentDayId],
      entryFeeIds: selectedQuestions.entryFees || [],
      cuisineIds: selectedQuestions.cuisines || [],
      alcoholIds: selectedQuestions.alcohols || [],
      nonAlcoholIds: selectedQuestions.nonAlcohols || [],
      regionsIds: selectedQuestions.regions || [],
      specialIds: selectedQuestions.specials || [],
      liveMusicIds: selectedQuestions.liveMusics || [],
      djClubIds: selectedQuestions.djClubs || [],
      cravingIds: selectedQuestions.cravings || [],
      foodPriceIds: selectedQuestions.foodPrices || [],
      dietaryIds: selectedQuestions.dietaries || [],
      eventActivitiesIds: selectedQuestions.eventActivities || [],
    };

    if (editingRow) {
      await apiCallVenuePreferencesUpdate({
        ...data,
        ratingId: editingRow._id,
      });

      toast.success("Preferences Updated Successfully");
    } else {
      await apiCallVenuePreferencesAdd(data);

      toast.success("Preferences Added Successfully");
    }

    await fetchDayPreference(currentDayId);
  };

  return (
    <Box p={4} pt={0} mt={3.5}>
      <Grid container spacing={5} alignItems="center" justifyContent={"end"}>
        <Grid container spacing={2}>
          <TextField
            select
            fullWidth
            style={{ width: "160px" }}
            label="Select Day"
            value={selectedDays[0] || ""}
            onChange={async (e) => {
              const dayId = e.target.value;
              setSelectedDays([dayId]);
              await fetchDayPreference(dayId);
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "black",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "black",
              },
              "& .MuiSelect-icon": {
                color: "black",
              },
            }}
            size="small"
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "black !important",
                      color: "white !important",
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "#E0E0E0",
                    },
                  },
                },
              },
            }}
          >
            {days.map((day) => (
              <MenuItem
                key={day._id}
                value={day._id}
                sx={{
                  color: selectedDays[0] === day._id ? "white" : "black",
                  backgroundColor:
                    selectedDays[0] === day._id ? "black" : "#FFF",
                  "&:hover": {
                    backgroundColor:
                      selectedDays[0] === day._id ? "#333333" : "#E0E0E0",
                  },
                }}
              >
                {day.dayName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3 }} />
      {loading ? (
        <Stack spacing={3}>
          <CommonShimmer />
        </Stack>
      ) : (
        <>

          {preferenceSections.map((section) => (
            <ChipSelector
              key={section.key}
              title={section.title}
              data={preferenceData?.[section.key] || []}
              labelKey={section.labelKey}
              selected={selectedQuestions?.[section.key] || []}
              setSelected={(val) =>
                setSelectedQuestions((prev) => ({
                  ...prev,
                  [section.key]: val,
                }))
              }
            />
          ))}

          <Stack direction="row" spacing={2} mt={3}>
            <Button style={{ color: "black" }}>Cancel</Button>
            <Button
              style={{ backgroundColor: "black", color: "white" }}
              variant="contained"
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Stack>
        </>
      )}

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
        closeButton={false}
      />
    </Box>
  );
};

export default VenuePreferences;
